import {
  type ArtifactEnvelope,
  isFileArtifact,
  isHtmlArtifact,
  isMarkdownArtifact,
  isPlotArtifact,
  isTableArtifact,
  isTextArtifact,
} from "./artifact-contract"
import { type DownloadData, artifactToDownloadData, safeJson, serializePayloadJson } from "./artifact-serialize"

interface ArtifactDownloadPayload {
  data: string
  extension: string
  mimeType: string
}

const ILLEGAL_FILENAME_CHARACTERS = /[/\\:*?"<>|]/g
const SURROUNDING_DOTS = /^\.+|[.\s]+$/g
const EMPTY_JSON_PAYLOADS = new Set(["{}", "[]"])
const FALLBACK_FILENAME_BASE = "artifact"

function csvEscapeField(field: string): string {
  return /[",\r\n]/.test(field) ? `"${field.replace(/"/g, "\"\"")}"` : field
}

function toCsv({ headers, rows }: DownloadData): string {
  return [headers, ...rows].map((row) => row.map(csvEscapeField).join(",")).join("\r\n")
}

export function artifactDownloadPayload(artifact: ArtifactEnvelope): ArtifactDownloadPayload | null {
  try {
    const payload = resolveArtifactDownloadPayload(artifact)
    return payload === null || payload.data.trim().length === 0 ? null : payload
  } catch {
    return null
  }
}

function resolveArtifactDownloadPayload(artifact: ArtifactEnvelope): ArtifactDownloadPayload | null {
  if (isFileArtifact(artifact)) return null

  if (isTableArtifact(artifact) || isPlotArtifact(artifact)) {
    const data = artifactToDownloadData(artifact)
    if (data !== null && data.rows.length > 0) return { data: toCsv(data), extension: "csv", mimeType: "text/csv" }
    if (!isPlotArtifact(artifact)) return null
    return jsonPayloadOrNull(safeJson(artifact.figure.data))
  }

  if (isTextArtifact(artifact)) return { data: artifact.text, extension: "txt", mimeType: "text/plain" }
  if (isMarkdownArtifact(artifact)) return { data: artifact.markdown, extension: "md", mimeType: "text/markdown" }
  if (isHtmlArtifact(artifact)) return { data: artifact.html, extension: "html", mimeType: "text/html" }

  return jsonPayloadOrNull(serializePayloadJson(artifact))
}

function jsonPayloadOrNull(data: string): ArtifactDownloadPayload | null {
  if (EMPTY_JSON_PAYLOADS.has(data)) return null
  return { data, extension: "json", mimeType: "application/json" }
}

export function artifactDownloadFilename(name: string, extension: string): string {
  const withoutIllegalCharacters = name.replace(ILLEGAL_FILENAME_CHARACTERS, "")
  const collapsed = withoutIllegalCharacters.replace(/\s+/g, " ").trim().replace(SURROUNDING_DOTS, "")
  const base = collapsed.length > 0 ? collapsed : FALLBACK_FILENAME_BASE
  return `${base}.${extension}`
}
